import { Link, useNavigate } from "react-router-dom";
import { Controller, useForm, FormProvider } from "react-hook-form";

import SearchInput, { GeoNames } from "../SearchInput";
import { getItemFromLocalStore, today } from "../../utils";

import Dropdown from "../DropDown";
import InputWithLabel from "../InputWithLabel";
import {
  CONTAINER_TYPE,
  TRADE_TERMS,
  // CONTAINER_TYPE,
  // FREIGHT_BASIS,
  // SHIPPING_TYPE,
  TRANSPORTATION_BY,
} from "../../constants/variable";
import Tooltip from "../Tooltip";
import {
  customs_clearance,
  insurance,
  // loading_date,
  // container_type,
  // frieght_basis,
  // loading_weight,
  // shipping_category,
  // shipping_type,
  transportation_by,
} from "../../constants/tooltipContents";
import {
  AirPlaneIcon,
  ExclamationCircleIcon,
  MapPinIcon,
  ShipIcon,
} from "../Icons";

import { useEffect } from "react";
import HsCodeInput from "../DealTracker/TradeFlowTrackerTabs/HsCodeInput";
// import AIFileAnalysisInput from "./AIFileAnalysisInput";
// import PartnerCompanySearchInput from "../PartnerCompany/PartnerCompanySearchInput";

export type SimpleBookingFormType = {
  from_json: Omit<GeoNames, "adminCode1" | "geonameId">;
  to_json: Omit<GeoNames, "adminCode1" | "geonameId">;
  freight_type: "ocean" | "air";
  transportation_by: string;
  // loading_date: string;
  customs_clearance: boolean;
  insurance: boolean;
  invoice_amount: number;
  order_file: { file: File | null }[];
  goods_category: string;
  pickup_date: string;
  container_type: string;
  container_quantity: string;
  weight: string;
  // partner_company_id: number | string;
  message: string;
  incoterm: string;
};

export default function SimpleBooking() {
  const navigate = useNavigate();

  const methods = useForm<SimpleBookingFormType>({
    defaultValues: {
      order_file: [{ file: null }],
      freight_type: "ocean",
    },
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    control,
    reset,
  } = methods;

  useEffect(() => {
    const persistedData = getItemFromLocalStore("cargo-details");

    if (persistedData) {
      reset(persistedData);
    }
  }, []);

  const selectedInsuranceService = watch("insurance");

  const freight_type = watch("freight_type");

  const errorMessages = Object.values(errors)
    .map((error: any) => error?.message)
    .filter(Boolean);

  const onSubmitHandler = async (values: SimpleBookingFormType) => {
    // console.log(values);

    localStorage.setItem("cargo-details", JSON.stringify(values));

    navigate("/logistics/quotes");
  };

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={handleSubmit(onSubmitHandler)}
        className="grid grid-cols-2 items-center gap-x-5 gap-y-2"
      >
        <div className="flex justify-between col-span-full">
          <fieldset className="flex items-center gap-4 border p-2 rounded-2xl">
            <label
              className={`flex items-center gap-2 px-8 py-2 rounded-2xl border shadow-sm transition cursor-pointer ${
                freight_type === "ocean"
                  ? "bg-blue-600 text-white"
                  : "bg-white text-black"
              }`}
            >
              <input
                type="radio"
                value="ocean"
                {...register("freight_type")}
                className="sr-only"
              />
              <ShipIcon />
              Ocean
            </label>

            <label
              className={`flex items-center gap-2 px-8 py-2 rounded-2xl border shadow-sm transition cursor-pointer ${
                freight_type === "air"
                  ? "bg-blue-600 text-white"
                  : "bg-white text-black"
              }`}
            >
              <input
                type="radio"
                value="air"
                {...register("freight_type")}
                className="sr-only"
              />
              <AirPlaneIcon />
              Air
            </label>
          </fieldset>

          <Controller
            name="goods_category"
            control={control}
            rules={{ required: "Category is required" }}
            render={({ field, fieldState }) => (
              <div className="max-w-[400px] w-full">
                <div className="flex items-center border-[0.66px] border-[#d0d5dd] h-12 rounded-xl shadow-sm bg-white text-sm">
                  <label className="block font-medium text-gray-700 border-r border-r-[#d0d5dd] h-12 p-4 rounded-xl">
                    Category
                  </label>

                  <HsCodeInput
                    value={field.value}
                    onChange={field.onChange}
                    className="outline-none w-full pl-5"
                    placeholder="Select Category"
                  />

                  <a
                    href="#"
                    className="text-blue-500 font-medium hover:underline ml-4"
                  >
                    HS CODES
                  </a>
                </div>
                <small className="text-red-500 h-[10px] block ml-1 text-xs">
                  {fieldState.error && fieldState.error.message}
                </small>
              </div>
            )}
          />
        </div>

        {/* <div className="col-span-full">
          <Controller
            control={control}
            name="partner_company_id"
            rules={{ required: "Please select a partner company" }}
            render={({ field, fieldState }) => (
              <div className="w-1/2 pr-3 pt-2">
                <label
                  className="text-[#344054] text-sm font-medium"
                  htmlFor="from"
                >
                  Partner Company
                </label>
                <PartnerCompanySearchInput
                  onChange={field.onChange}
                  value={field.value}
                />
                <small className="text-red-500 h-[10px] block ml-1">
                  {fieldState.error && fieldState.error.message}
                </small>
              </div>
            )}
          />
        </div> */}

        <Controller
          name="from_json"
          control={control}
          rules={{ required: "Please enter a source" }}
          render={({ field }) => (
            <div className="w-full">
              <label
                className="text-[#344054] text-sm font-medium"
                htmlFor="from"
              >
                From
              </label>
              <SearchInput
                placeholder="Source"
                name="from"
                initialCity={
                  field.value
                    ? `${field.value?.name}, ${field.value?.countryCode}`
                    : ""
                }
                icon={<MapPinIcon className="size-5 text-gray-700" />}
                className="input-new !pl-[40px]"
                onChange={field.onChange}
              />

              <small className="text-red-500 h-[10px] block ml-1">
                {errors.from_json && errors.from_json.message}
              </small>
            </div>
          )}
        />

        <Controller
          control={control}
          name="to_json"
          rules={{ required: "Please enter a destination" }}
          render={({ field }) => (
            <div className="w-full">
              <label
                className="text-[#344054] text-sm font-medium"
                htmlFor="to"
              >
                To
              </label>
              <SearchInput
                placeholder="Destination"
                name="to"
                initialCity={
                  field.value
                    ? `${field.value?.name}, ${field.value?.countryCode}`
                    : ""
                }
                icon={<MapPinIcon className="size-5 text-gray-700" />}
                className="input-new !pl-[40px]"
                onChange={field.onChange}
              />

              <small className="text-red-500 h-[10px] block ml-1">
                {errors.to_json && errors.to_json.message}
              </small>
            </div>
          )}
        />

        {freight_type === "ocean" && (
          <Dropdown
            element={<Tooltip content={transportation_by} />}
            label="Transportation By"
            placeholder="Select transportation by"
            options={TRANSPORTATION_BY}
            {...register("transportation_by", {
              required: "Select a Tansportation type",
            })}
            error={errors.transportation_by?.message}
          />
        )}

        {/* <InputWithLabel
          element={<Tooltip content={loading_date} />}
          label="Loading Date"
          type="date"
          onClick={(e) => e.currentTarget.showPicker()}
          min={today}
          {...register("loading_date", { required: "Select a Loading date" })}
          error={errors.loading_date?.message}
        /> */}

        {/* <AIFileAnalysisInput /> */}
        {freight_type === "ocean" && (
          <>
            <Dropdown
              label="Container Type"
              placeholder="Select container type"
              options={CONTAINER_TYPE}
              {...register("container_type", {
                required: "Select container type",
              })}
              error={errors.container_type?.message}
            />

            <InputWithLabel
              label="Container Quantity"
              placeholder="Enter container quantity"
              type="text"
              {...register("container_quantity", {
                required: "Container quantity required",
              })}
              error={errors.container_quantity?.message}
            />
          </>
        )}

        <InputWithLabel
          label="Pickup Date"
          type="date"
          onClick={(e) => e.currentTarget.showPicker()}
          min={today}
          {...register("pickup_date", { required: "Select a pickup date" })}
          error={errors.pickup_date?.message}
        />

        <InputWithLabel
          label="Weight (Kgs)"
          placeholder="Enter weight"
          type="text"
          {...register("weight", {
            required: "Weight is required",
          })}
          error={errors.weight?.message}
        />

        {freight_type === "ocean" && (
          <Dropdown
            label="Incoterm"
            placeholder="Select type"
            options={TRADE_TERMS}
            {...register("incoterm", {
              required: "Incoterm is required",
            })}
            error={errors.incoterm?.message}
          />
        )}

        <div className="col-span-full">
          <label className="block text-[#344054] text-sm font-medium">
            Message
          </label>
          <textarea
            className="input-new"
            placeholder="Enter Message"
            {...register("message")}
          ></textarea>
        </div>

        <div className="text-[#344054] col-span-full">
          <h3 className="mb-1 mt-4 font-medium">Additional Service:</h3>

          <div className="flex gap-5">
            <div className="w-full space-y-2">
              <div className="flex justify-end">
                <Tooltip content={customs_clearance} />
              </div>

              <label className="w-full border p-2 rounded-lg flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="size-4"
                  {...register("customs_clearance")}
                />
                <svg
                  className="size-4"
                  viewBox="0 0 11 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M4.5001 0.5L5.0001 1.5H10.0001L10.5001 0.5H4.5001ZM5.0001 2V3.25C5.0001 3.25 5.0001 6 7.5001 6C10.0001 6 10.0001 3.25 10.0001 3.25V2H5.0001ZM0.9841 3.486C0.719224 3.49021 0.466843 3.59935 0.282357 3.78946C0.0978713 3.97957 -0.0036423 4.23512 9.99123e-05 4.5V8.5C-2.42119e-05 8.64982 0.0335183 8.79776 0.0982491 8.93288C0.16298 9.06799 0.257244 9.18684 0.374077 9.28063C0.49091 9.37443 0.627324 9.44077 0.773241 9.47476C0.919158 9.50875 1.07085 9.50951 1.2171 9.477L4.0001 8.857V13.5L9.8791 6.57C9.73706 6.52644 9.58964 6.50288 9.4411 6.5H5.5001L2.0001 7.254V4.5C2.00199 4.36615 1.97699 4.23328 1.92657 4.10927C1.87616 3.98526 1.80136 3.87263 1.70661 3.77807C1.61186 3.6835 1.49909 3.60893 1.37498 3.55876C1.25087 3.50859 1.11795 3.48384 0.9841 3.486ZM10.7481 7.211L5.4121 13.5H11.0001V8.059C11.0001 7.745 10.9061 7.455 10.7481 7.211Z"
                    fill="black"
                  />
                </svg>
                Customs Clearance
              </label>
            </div>

            <div className="w-full space-y-2 pb-4">
              <div className="flex justify-end">
                <Tooltip content={insurance} />
              </div>
              <label className="w-full border p-2 rounded-lg flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="size-4"
                  {...register("insurance")}
                />
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  className="size-4 text-black"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.603 3.799A4.49 4.49 0 0 1 12 2.25c1.357 0 2.573.6 3.397 1.549a4.49 4.49 0 0 1 3.498 1.307 4.491 4.491 0 0 1 1.307 3.497A4.49 4.49 0 0 1 21.75 12a4.49 4.49 0 0 1-1.549 3.397 4.491 4.491 0 0 1-1.307 3.497 4.491 4.491 0 0 1-3.497 1.307A4.49 4.49 0 0 1 12 21.75a4.49 4.49 0 0 1-3.397-1.549 4.49 4.49 0 0 1-3.498-1.306 4.491 4.491 0 0 1-1.307-3.498A4.49 4.49 0 0 1 2.25 12c0-1.357.6-2.573 1.549-3.397a4.49 4.49 0 0 1 1.307-3.497 4.49 4.49 0 0 1 3.497-1.307Zm7.007 6.387a.75.75 0 1 0-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 0 0-1.06 1.06l2.25 2.25a.75.75 0 0 0 1.14-.094l3.75-5.25Z"
                    clipRule="evenodd"
                  />
                </svg>
                Insurance
              </label>
              {selectedInsuranceService && (
                <InputWithLabel
                  label="Invoice Amount"
                  placeholder="Enter value"
                  type="number"
                  {...register("invoice_amount", {
                    required: "Enter a invoice amount",
                  })}
                  error={errors.invoice_amount?.message}
                />
              )}
            </div>
          </div>
        </div>

        <hr className="col-span-full" />

        {errorMessages.length > 0 && (
          <div className="col-span-full mb-4">
            <div className="rounded-xl border border-red-200 bg-red-50 p-4">
              <div className="flex items-start gap-3">
                {/* Icon */}
                <div className="bg-red-100 text-red-600 rounded-full p-1.5 mt-0.5">
                  <ExclamationCircleIcon className="size-4" strokeWidth={2} />
                </div>

                <div>
                  <p className="text-sm font-semibold text-red-700">
                    {errorMessages.length} required field
                    {errorMessages.length > 1 ? "s" : ""} missing
                  </p>

                  <ul className="mt-2 text-xs text-red-600 space-y-1">
                    {errorMessages.map((msg, index) => (
                      <li key={index}>• {msg}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="col-span-full pt-5 flex justify-between items-center">
          <Link
            className="w-[87px] h-[44px] flex items-center justify-center border border-[#D0D5DD] text-[#344054] font-medium rounded-lg"
            to={"/logistics"}
          >
            Back
          </Link>

          <button
            type="submit"
            // disabled={isSubmitting}
            className="w-[146px] h-[44px] rounded-md bg-custom-blue-medium text-white font-medium"
          >
            Continue
          </button>
        </div>
      </form>
    </FormProvider>
  );
}
